import React, { useState, useEffect, useCallback } from 'react';
import { dashboardAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import LowCreditBanner from '../components/LowCreditBanner';

const TERRA = '#0076A3';
const INK = '#002A38';
const MUTED = '#78716C';
const FAINT = '#A8A29E';
const STONE = '#57534E';
const LINE = '#ECE7DE';
const CANVAS = '#F6F1EA';
const WARM_YELLOW = '#FAD9A0';
const LIGHT = { green: '#16A34A', amber: '#D97706', red: '#DC2626' };
const TOOLTIP_STYLE = { borderRadius: 12, border: `1px solid ${LINE}`, fontFamily: 'DM Sans' };
const CHART_MARGIN = { left: 10 };
const AXIS_TICK = { fill: FAINT, fontSize: 11 };
const AXIS_TICK_CAT = { fill: STONE, fontSize: 12 };

const cardStyle = { background: '#fff', border: `1px solid ${LINE}`, borderRadius: 16, padding: '1.5rem', boxShadow: '0 1px 3px rgba(28,25,23,0.04)' };
const display = { fontFamily: "'Cabinet Grotesk', 'DM Sans', sans-serif" };

const ComingSoon = () => (
  <span data-testid="coming-soon-badge" style={{ fontSize: '0.5625rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: TERRA, background: 'rgba(240, 80, 0,0.1)', border: '1px solid rgba(240, 80, 0,0.3)', borderRadius: 999, padding: '0.0625rem 0.5rem' }}>Coming soon</span>
);

const SectionTitle = ({ children }) => (
  <div style={{ marginBottom: '1rem' }}>
    <h2 style={{ ...display, fontSize: '1.0625rem', fontWeight: 800, color: INK, margin: 0, letterSpacing: '-0.02em' }}>{children}</h2>
    <div style={{ width: 36, height: 3, borderRadius: 999, background: `linear-gradient(90deg, ${TERRA}, ${WARM_YELLOW})`, marginTop: 6 }} />
  </div>
);

const Kpi = ({ label, value, testid }) => (
  <div data-testid={testid} className="kpi-tile" style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 16, padding: '1.25rem 1.375rem', position: 'relative' }}>
    <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: FAINT, marginBottom: 10 }}>{label}</div>
    <div style={{ ...display, fontSize: '2rem', fontWeight: 800, color: INK, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    <div style={{ position: 'absolute', top: 18, right: 18, width: 8, height: 8, borderRadius: '50%', background: TERRA }} />
  </div>
);

export default function CommandCenterPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await dashboardAPI.getCommandCenter();
      setData(res.data);
      if (isRefresh) toast.success('Command Center refreshed');
    } catch (err) {
      toast.error('Failed to load Command Center');
    } finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: MUTED }}>Loading Command Center...</div>;
  if (!data) return null;

  const { todays_work, funnel, active_jobs, candidate_pipeline, productivity, feature_adoption, adoption_pct, ai_intelligence, scorecard, coming_soon } = data;

  return (
    <div data-testid="command-center-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Prioritise work, track hiring performance, and maximise platform value.</p>
        </div>
        <button data-testid="cc-refresh-btn" onClick={() => load(true)} disabled={refreshing}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: INK, color: '#fff', border: 'none', borderRadius: 999, padding: '0.625rem 1.25rem', fontSize: '0.8125rem', fontWeight: 600, cursor: refreshing ? 'default' : 'pointer', opacity: refreshing ? 0.7 : 1, boxShadow: '0 8px 20px -10px rgba(28,25,23,0.5)', transition: 'transform 0.2s, background 0.2s' }}
          onMouseEnter={(e) => { if (!refreshing) { e.currentTarget.style.background = '#292524'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
          onMouseLeave={(e) => { e.currentTarget.style.background = INK; e.currentTarget.style.transform = 'none'; }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }}>
            <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <LowCreditBanner />

      {/* AI insight banner */}
      <div data-testid="cc-ai-insight" style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', background: 'linear-gradient(100deg, #002A38 0%, #2C2622 100%)', borderRadius: 16, padding: '1rem 1.375rem', marginBottom: '1.5rem', boxShadow: '0 16px 34px -22px rgba(28,25,23,0.6)' }}>
        <span style={{ width: 30, height: 30, borderRadius: '50%', background: TERRA, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.75rem', flexShrink: 0 }}>AI</span>
        <span style={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.9375rem' }}>{todays_work.ai_insight}</span>
      </div>

      {/* Section 1: Today's Work */}
      <SectionTitle>Today's Work</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1rem', marginBottom: '2.25rem' }}>
        <Kpi testid="cc-open-jobs" label="Open Jobs" value={todays_work.open_jobs} />
        <Kpi testid="cc-interviews-today" label="Interviews Today" value={todays_work.interviews_today} />
        <Kpi testid="cc-pending-assessments" label="Pending Assessments" value={todays_work.pending_assessments} />
        <Kpi testid="cc-pending-offers" label="Pending Offers" value={todays_work.pending_offers} />
        <Kpi testid="cc-awaiting-action" label="Awaiting Action" value={todays_work.candidates_awaiting_action} />
      </div>

      {/* Section 2 & 9: Funnel + Scorecard */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem', marginBottom: '2.25rem' }} className="cc-funnel-grid">
        <Card style={cardStyle}>
          <SectionTitle>Hiring Funnel</SectionTitle>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={funnel} layout="vertical" margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke={LINE} horizontal={false} />
              <XAxis type="number" tick={AXIS_TICK} allowDecimals={false} />
              <YAxis type="category" dataKey="stage" tick={AXIS_TICK_CAT} width={80} />
              <Tooltip formatter={(v, n, p) => [`${v} candidates (${p.payload.conversion_pct}% conv.)`, p.payload.stage]} contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(240, 80, 0,0.06)' }} />
              <Bar dataKey="count" fill={TERRA} radius={[0, 4, 4, 0]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card style={cardStyle} data-testid="cc-scorecard">
          <SectionTitle>Recruiter Score</SectionTitle>
          <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
            <div style={{ ...display, fontSize: '3.5rem', fontWeight: 800, color: TERRA, lineHeight: 1 }}>{scorecard.overall}</div>
            <div style={{ fontSize: '0.75rem', color: FAINT }}>out of 100</div>
          </div>
          {[['Feature Usage', scorecard.feature_usage], ['Candidate Quality', scorecard.quality], ['Hiring Velocity', scorecard.velocity]].map(([label, val]) => (
            <div key={label} style={{ marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: STONE, marginBottom: 3 }}><span>{label}</span><span style={{ fontWeight: 700 }}>{val}</span></div>
              <div style={{ height: 6, background: CANVAS, borderRadius: 3 }}><div style={{ width: `${val}%`, height: '100%', background: TERRA, borderRadius: 3 }} /></div>
            </div>
          ))}
        </Card>
      </div>

      {/* Section 3: Active Jobs */}
      <SectionTitle>Active Jobs</SectionTitle>
      <Card style={{ ...cardStyle, padding: 0, marginBottom: '2.25rem', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table data-testid="cc-active-jobs-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ background: CANVAS, textAlign: 'left', color: MUTED }}>
                {['', 'JD ID', 'Job Title', 'Recruiter', 'Open Days', 'Candidates', 'Shortlisted', 'Interviewed', 'Offers', 'Joined'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {active_jobs.length === 0 ? (
                <tr><td colSpan={10} style={{ padding: '2rem', textAlign: 'center', color: FAINT }}>No active jobs yet.</td></tr>
              ) : active_jobs.map((j) => (
                <tr key={j.jd_id} className="candidate-row" style={{ borderTop: `1px solid ${LINE}` }}>
                  <td style={{ padding: '0.75rem 1rem' }}><span title={j.traffic_light} style={{ width: 10, height: 10, borderRadius: '50%', background: LIGHT[j.traffic_light], display: 'inline-block' }} /></td>
                  <td style={{ padding: '0.75rem 1rem', fontFamily: "'JetBrains Mono', monospace", color: FAINT }}>{j.jd_id}</td>
                  <td style={{ padding: '0.75rem 1rem', fontWeight: 700, color: INK }}>{j.title}</td>
                  <td style={{ padding: '0.75rem 1rem', color: STONE }}>{j.recruiter}</td>
                  <td style={{ padding: '0.75rem 1rem', color: STONE }}>{j.open_days}d</td>
                  <td style={{ padding: '0.75rem 1rem', color: STONE }}>{j.candidates}</td>
                  <td style={{ padding: '0.75rem 1rem', color: STONE }}>{j.shortlisted}</td>
                  <td style={{ padding: '0.75rem 1rem', color: STONE }}>{j.interviewed}</td>
                  <td style={{ padding: '0.75rem 1rem', color: STONE }}>{j.offer_released}</td>
                  <td style={{ padding: '0.75rem 1rem', color: STONE }}>{j.joined}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Section 5: Productivity */}
      <SectionTitle>Productivity</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2.25rem' }}>
        {[
          ['Jobs Managed', productivity.jobs_managed], ['Candidates Reviewed', productivity.candidates_reviewed],
          ['Resumes Screened', productivity.resumes_screened], ['Assessments Generated', productivity.assessments_generated],
          ['AI Interviews', productivity.ai_interviews_conducted], ['Emails Automated', productivity.emails_automated],
          ['BGV Initiated', productivity.bgv_initiated], ['Offers Released', productivity.offers_released],
        ].map(([label, val]) => (
          <div key={label} style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 16, padding: '0.875rem 1rem' }}>
            <div style={{ ...display, fontSize: '1.375rem', fontWeight: 800, color: INK }}>{val}</div>
            <div style={{ fontSize: '0.6875rem', color: MUTED }}>{label}</div>
          </div>
        ))}
        <div style={{ background: INK, borderRadius: 16, padding: '0.875rem 1rem' }}>
          <div style={{ ...display, fontSize: '1.375rem', fontWeight: 800, color: WARM_YELLOW }}>{productivity.hours_saved}h</div>
          <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.7)' }}>Hours Saved (AI est.)</div>
        </div>
      </div>

      {/* Section 6 + 8 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2.25rem' }} className="cc-funnel-grid">
        <Card style={cardStyle} data-testid="cc-feature-adoption">
          <SectionTitle>Feature Adoption — {adoption_pct}%</SectionTitle>
          {feature_adoption.map(f => (
            <div key={f.feature} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: `1px solid ${LINE}` }}>
              <span style={{ fontSize: '0.8125rem', color: '#44403C' }}>{f.feature}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.75rem', color: FAINT }}>{f.count}</span>
                <span style={{ fontSize: '0.625rem', fontWeight: 700, padding: '0.0625rem 0.5rem', borderRadius: 999, background: f.used ? 'rgba(22,163,74,0.12)' : 'rgba(168,162,158,0.16)', color: f.used ? '#16A34A' : FAINT }}>{f.used ? 'Active' : 'Not used'}</span>
              </span>
            </div>
          ))}
        </Card>

        <Card style={cardStyle} data-testid="cc-ai-intelligence">
          <SectionTitle>AI Hiring Intelligence</SectionTitle>
          {ai_intelligence.jobs_at_risk.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#DC2626', marginBottom: 6 }}>Jobs at Risk</div>
              {ai_intelligence.jobs_at_risk.map((j, i) => (
                <div key={`${j.title}-${j.open_days}-${i}`} style={{ fontSize: '0.8125rem', color: STONE, padding: '0.25rem 0' }}>• {j.title} <span style={{ color: FAINT }}>({j.open_days}d open, {j.candidates} candidates)</span></div>
              ))}
            </div>
          )}
          <div style={{ fontSize: '0.75rem', fontWeight: 700, color: TERRA, marginBottom: 6 }}>Suggested Next Actions</div>
          {ai_intelligence.suggested_actions.map((a, i) => (
            <div key={`action-${i}`} style={{ display: 'flex', gap: '0.5rem', fontSize: '0.8125rem', color: STONE, padding: '0.25rem 0' }}>
              <span style={{ color: TERRA, fontWeight: 800 }}>›</span><span>{a}</span>
            </div>
          ))}
        </Card>
      </div>

      {/* Candidate pipeline quick stats */}
      <SectionTitle>Candidate Pipeline</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '2.25rem' }}>
        {[['Total', candidate_pipeline.total], ['New (7d)', candidate_pipeline.new], ['In Screening', candidate_pipeline.screening], ['In Interview', candidate_pipeline.interview], ['Rejected', candidate_pipeline.rejected], ['Withdrawn', candidate_pipeline.withdrawn]].map(([label, val]) => (
          <div key={label} style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 16, padding: '0.875rem 1rem' }}>
            <div style={{ ...display, fontSize: '1.375rem', fontWeight: 800, color: INK }}>{val}</div>
            <div style={{ fontSize: '0.6875rem', color: MUTED }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Coming soon roadmap */}
      <Card style={{ background: '#FBF7F1', border: `1px dashed ${'#E3D2C2'}`, borderRadius: 16, padding: '1.5rem' }} data-testid="cc-coming-soon">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1rem' }}>
          <h2 style={{ ...display, fontSize: '1rem', fontWeight: 800, color: INK, margin: 0 }}>Coming Soon</h2>
          <ComingSoon />
        </div>
        <p style={{ fontSize: '0.8125rem', color: MUTED, marginBottom: '1rem' }}>These insights need data CHIRE will start capturing in upcoming phases (hiring-manager feedback, SLAs, source & salary, closure dates).</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
          {Object.entries(coming_soon).map(([section, items]) => (
            <div key={section}>
              <div style={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'capitalize', color: STONE, marginBottom: 6 }}>{section.replace(/_/g, ' ')}</div>
              {items.map(it => <div key={it} style={{ fontSize: '0.75rem', color: FAINT, padding: '0.125rem 0' }}>• {it}</div>)}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
